import { ARCHIVE_SEARCH_MODES, ARCHIVE_SEARCH_SORTS } from "./archiveSearchProtocol.js";
import { normalizeArchiveSearchIndex } from "./archiveSearchIndex.js";
import { clampSearchQuery, meaningfulSearchTokens, normalizeSearchText, titleMatchesByPrefix } from "./searchText.js";
import { expandQueryTokens, getQueryTokenMatchNote, getQueryTokenWeight } from "./synonyms.js";

function fieldContainsPhrase(field = "", query = "") {
  if (!field || !query) return false;
  return ` ${field} `.includes(` ${query} `);
}

function fieldContainsToken(fieldTokens = [], token = "") {
  return fieldTokens.includes(token);
}

function scoreField({
  field = "",
  fieldTokens = [],
  query = "",
  queryTokens = [],
  tokenCount = 0,
  exact = 1,
  contains = 0.75,
  tokenWeight = 0.18,
  prefix = false,
}) {
  if (!field || !query) return null;
  if (field === query) {
    return { score: exact, matchKind: "exact" };
  }
  if (fieldContainsPhrase(field, query)) {
    return { score: contains, matchKind: "contains" };
  }
  if (prefix && titleMatchesByPrefix(field, query)) {
    return { score: contains - 0.05, matchKind: "contains" };
  }

  let matchedCount = 0;
  let matchedWeight = 0;
  let matchedEntry = null;
  const seenTokens = new Set();
  for (const entry of queryTokens) {
    if (!fieldContainsToken(fieldTokens, entry.token) || seenTokens.has(entry.token)) continue;
    seenTokens.add(entry.token);
    matchedCount += 1;
    matchedWeight += getQueryTokenWeight(entry);
    if (
      !matchedEntry ||
      getQueryTokenWeight(entry) > getQueryTokenWeight(matchedEntry) ||
      (getQueryTokenWeight(entry) === getQueryTokenWeight(matchedEntry) && entry.kind === "exact" && matchedEntry.kind !== "exact")
    ) {
      matchedEntry = entry;
    }
  }

  if (tokenCount > 1 && matchedCount < 2) {
    return null;
  }
  if (!matchedCount) return null;

  return {
    score: Math.min(contains - 0.05, matchedWeight * tokenWeight),
    matchKind: matchedEntry?.kind || "token",
    matchEntry: matchedEntry,
  };
}

function selectedTagMatches(show = {}, selectedTags = [], tagsBySlug = new Map()) {
  if (selectedTags.length === 0) return { ok: true, labels: [] };
  const normalizedShowTags = show._search?.tagSet || new Set((show.tasteTags || []).map(normalizeSearchText));
  const labels = [];
  for (const slug of selectedTags) {
    const tag = tagsBySlug.get(slug);
    const label = tag?.tag || slug;
    if (!normalizedShowTags.has(normalizeSearchText(label))) {
      return { ok: false, labels: [] };
    }
    labels.push(label);
  }
  return { ok: true, labels };
}

function explainLexicalMatch(show, query, selectedTagLabels = [], bestMatch = null) {
  if (selectedTagLabels.length > 0) {
    return `Matches ${selectedTagLabels.slice(0, 2).join(", ")}`;
  }
  if (bestMatch?.fieldType === "title" && bestMatch.matchKind === "exact") {
    return "Exact title match";
  }
  if (bestMatch?.fieldType === "title" && bestMatch.matchKind === "contains") {
    return "Title match";
  }
  const normalizedQuery = normalizeSearchText(query);
  const tagMatch = (show._search?.tasteTagEntries || []).find((tag) => fieldContainsPhrase(tag.text, normalizedQuery));
  if (tagMatch) return `Matches ${tagMatch.label}`;
  if (fieldContainsPhrase(show._search?.title, normalizedQuery)) return "Exact title match";
  const genreMatch = (show._search?.genreEntries || []).find((genre) => fieldContainsPhrase(genre.text, normalizedQuery));
  if (genreMatch) return `Matches ${genreMatch.label}`;
  if (show.source && fieldContainsPhrase(show._search?.source, normalizedQuery)) return `Matches ${show.source}`;
  if (show.monthLabel && fieldContainsPhrase(show._search?.month, normalizedQuery)) return `Matches ${show.monthLabel}`;
  if (bestMatch?.label) {
    return `Matches ${bestMatch.label}${getQueryTokenMatchNote(bestMatch.matchEntry)}`;
  }
  return "Matches your search";
}

function getLexicalMatchTags(selectedTagLabels = [], bestMatch = null) {
  if (selectedTagLabels.length > 0) {
    return selectedTagLabels.filter(Boolean).slice(0, 2);
  }
  if (bestMatch?.fieldType === "tag" && bestMatch.label) {
    return [bestMatch.label];
  }
  return [];
}

export function sortSearchResults(results = [], sort = ARCHIVE_SEARCH_SORTS.RELEVANCE) {
  const sorted = [...results];
  if (sort === ARCHIVE_SEARCH_SORTS.NEWEST) {
    sorted.sort((left, right) =>
      String(right.releaseDate || "").localeCompare(String(left.releaseDate || "")) ||
      String(left.title || "").localeCompare(String(right.title || ""))
    );
    return sorted;
  }
  if (sort === ARCHIVE_SEARCH_SORTS.RATING) {
    sorted.sort((left, right) =>
      (Number(right.rating) || -1) - (Number(left.rating) || -1) ||
      String(right.releaseDate || "").localeCompare(String(left.releaseDate || "")) ||
      String(left.title || "").localeCompare(String(right.title || ""))
    );
    return sorted;
  }
  sorted.sort((left, right) =>
    (Number(right.score) || 0) - (Number(left.score) || 0) ||
    String(right.releaseDate || "").localeCompare(String(left.releaseDate || "")) ||
    String(left.title || "").localeCompare(String(right.title || ""))
  );
  return sorted;
}

export function runLexicalSearch(index, {
  query = "",
  selectedTags = [],
  sort = ARCHIVE_SEARCH_SORTS.RELEVANCE,
  limit = 48,
  minScore = 0.18,
} = {}) {
  const normalizedIndex = index?.showsBySlug ? index : normalizeArchiveSearchIndex(index);
  const rawQuery = clampSearchQuery(query);
  const normalizedQuery = normalizeSearchText(rawQuery);
  const tokens = meaningfulSearchTokens(rawQuery);
  const queryTokens = expandQueryTokens(tokens, normalizedIndex);
  const hasQuery = normalizedQuery.length >= 2;
  const hasTags = selectedTags.length > 0;

  if (!hasQuery && !hasTags) {
    return {
      mode: ARCHIVE_SEARCH_MODES.LEXICAL,
      query: rawQuery,
      topScore: 0,
      results: [],
    };
  }

  // A tag narrows a typed search: without this the tag's base score listed every
  // tagged show whether or not it matched the words.
  const queryMatchSlugs = hasQuery && hasTags
    ? new Set(runLexicalSearch(normalizedIndex, { query: rawQuery, sort, limit: Infinity, minScore }).results.map((show) => show.slug))
    : null;

  const results = [];
  for (const show of normalizedIndex.shows || []) {
    if (queryMatchSlugs && !queryMatchSlugs.has(show.slug)) continue;
    const tagState = selectedTagMatches(show, selectedTags, normalizedIndex.tagsBySlug);
    if (!tagState.ok) continue;

    let score = hasTags ? 0.55 + (tagState.labels.length * 0.08) : 0;
    let bestMatch = tagState.labels.length > 0 ? { fieldType: "tags", label: tagState.labels.slice(0, 2).join(", ") } : null;
    if (hasQuery) {
      const titleMatch = scoreField({
        field: show._search.title,
        fieldTokens: show._search.titleTokens,
        query: normalizedQuery,
        queryTokens,
        tokenCount: tokens.length,
        exact: 1,
        contains: 0.9,
        tokenWeight: 0.2,
        prefix: true,
      });
      if (titleMatch && titleMatch.score > score) {
        score = titleMatch.score;
        bestMatch = { fieldType: "title", label: show.title, ...titleMatch };
      }

      for (const [indexValue, tag] of show._search.tasteTagEntries.entries()) {
        const tagMatch = scoreField({
          field: tag.text,
          fieldTokens: show._search.tagTokens[indexValue],
          query: normalizedQuery,
          queryTokens,
          tokenCount: tokens.length,
          exact: 0.94,
          contains: 0.86,
          tokenWeight: 0.26,
        });
        if (tagMatch && tagMatch.score > score) {
          score = tagMatch.score;
          bestMatch = { fieldType: "tag", label: tag.label, ...tagMatch };
        }
      }

      for (const [indexValue, genre] of show._search.genreEntries.entries()) {
        const genreMatch = scoreField({
          field: genre.text,
          fieldTokens: show._search.genreTokens[indexValue],
          query: normalizedQuery,
          queryTokens,
          tokenCount: tokens.length,
          exact: 0.72,
          contains: 0.62,
          tokenWeight: 0.15,
        });
        if (genreMatch && genreMatch.score > score) {
          score = genreMatch.score;
          bestMatch = { fieldType: "genre", label: genre.label, ...genreMatch };
        }
      }

      const sourceMatch = scoreField({
        field: show._search.source,
        fieldTokens: show._search.sourceTokens,
        query: normalizedQuery,
        queryTokens,
        tokenCount: tokens.length,
        exact: 0.68,
        contains: 0.58,
        tokenWeight: 0.14,
      });
      if (sourceMatch && sourceMatch.score > score) {
        score = sourceMatch.score;
        bestMatch = { fieldType: "source", label: show.source, ...sourceMatch };
      }

      const monthMatch = scoreField({
        field: show._search.month,
        fieldTokens: show._search.monthTokens,
        query: normalizedQuery,
        queryTokens,
        tokenCount: tokens.length,
        exact: 0.64,
        contains: 0.54,
        tokenWeight: 0.12,
      });
      if (monthMatch && monthMatch.score > score) {
        score = monthMatch.score;
        bestMatch = { fieldType: "month", label: show.monthLabel || show.yearMonth, ...monthMatch };
      }

      const allMatch = scoreField({
        field: show._search.all,
        fieldTokens: show._search.allTokens,
        query: normalizedQuery,
        queryTokens,
        tokenCount: tokens.length,
        exact: 0.52,
        contains: 0.42,
        tokenWeight: 0.1,
      });
      if (allMatch && allMatch.score > score) {
        score = allMatch.score;
        bestMatch = { fieldType: "all", label: null, ...allMatch };
      }
    }

    if (hasQuery && normalizedQuery === show._search.title) {
      score = 1.5;
      bestMatch = { fieldType: "title", label: show.title, matchKind: "exact" };
    }

    if (score < minScore) continue;
    results.push({
      ...show,
      score: Number(score.toFixed(4)),
      matchMode: ARCHIVE_SEARCH_MODES.LEXICAL,
      matchTags: getLexicalMatchTags(tagState.labels, bestMatch),
      matchReason: explainLexicalMatch(show, rawQuery, tagState.labels, bestMatch),
    });
  }

  const sorted = sortSearchResults(results, sort);
  return {
    mode: ARCHIVE_SEARCH_MODES.LEXICAL,
    query: rawQuery,
    topScore: sorted[0]?.score || 0,
    results: sorted.slice(0, limit),
  };
}

export function shouldTriggerSemanticSearch(query, lexicalResult = {}) {
  const tokens = meaningfulSearchTokens(query);
  if (tokens.length < 2) return false;
  return (lexicalResult?.topScore || 0) < 0.4;
}

export function shouldSubmitSemanticSearch(query) {
  return meaningfulSearchTokens(query).length >= 1;
}
